import { useTranslation } from '@jq4race/i18n';
import type { OutboxState } from '@jq4race/shared';
import { Button, Card, CardContent } from '@jq4race/ui';
import { useLiveQuery } from 'dexie-react-hooks';
import { AlertTriangle, CheckCircle2, Clock, Loader2, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { Identity } from '../../../auth/domain/resolve-identity';
import { EmptyState } from '../../../shell/ui/EmptyState';
import { PageHeader } from '../../../shell/ui/PageHeader';
import { acknowledgeDurableWarning } from '../../application/acknowledge-durable-warning';
import { STUCK_SYNCING_RESET_THRESHOLD_MS } from '../../application/foreground-drainer';
import { resolveRejected } from '../../application/resolve-rejected';
import { isBlockedBySubscription } from '../../domain/classify-sync-error';
import { type OutboxItem, UNSYNCED_OUTBOX_STATES } from '../../domain/outbox-item';
import {
  computePendingSummary,
  hasDurableWarning,
  isActivelyPending,
  isNearStuckThreshold,
  isQueueStale,
  isRetrying,
} from '../../domain/pending-warnings';
import { offlineDb } from '../../infrastructure/dexie/offline-db';
import { DexieOutboxRepository } from '../../infrastructure/dexie-outbox.repository';
import { reportQueueStale, tagPendingCount } from '../../infrastructure/observability';
import { shouldWarnOldestAge, shouldWarnPendingCount } from '../../infrastructure/storage-guard';
import { RejectedItemCard } from '../components/RejectedItemCard';
import { useStorageGuard } from '../use-storage-guard';

// Module-scope singleton — same pattern as CheckinPage.tsx's own
// outboxRepo/blobStore (one Dexie-backed repo instance for the app's
// lifetime, never re-created per render).
const outboxRepo = new DexieOutboxRepository(offlineDb);

// LOW fix (adversarial review) — MODULE scope, not a `useRef`. A `useRef`
// resets to `false` on every PendingItemsPage REMOUNT (navigating away
// from /pending and back), which let a queue that stayed stale the whole
// time re-report to Sentry on every revisit — exactly the spam the "once
// per staleness episode" gate exists to prevent, for exactly the workflow
// this page exists for (an operator repeatedly checking /pending while
// offline). Module scope survives remounts within the same page load;
// still cleared the moment the queue genuinely stops being stale, so a
// later, genuinely NEW episode is still reported.
let queueStaleReported = false;

/** Test-only reset hook — see PendingItemsPage.spec.tsx's own beforeEach. */
export function __resetQueueStaleGateForTests(): void {
  queueStaleReported = false;
}

function stateLabelKey(state: OutboxState): string {
  switch (state) {
    case 'pending':
    case 'syncing':
      return 'pendingItems.stateSyncing';
    case 'rejected':
      return 'pendingItems.stateRejected';
    case 'needs-auth':
      return 'pendingItems.stateNeedsAuth';
    case 'held':
      return 'pendingItems.stateHeld';
    default:
      return 'pendingItems.stateSyncing';
  }
}

interface PendingItemsPageProps {
  /**
   * pwa-offline-first Slice 6 (coordinator finding #4) — resolved by the
   * `/pending` route wrapper. `null` (identity not resolved yet) always
   * shows the empty state rather than querying unscoped.
   */
  identity: Identity | null;
}

/**
 * PendingItemsPage (task 6.8, Slice 7's conflict UX + durable warnings) —
 * read-only-ish list of every outbox item still needing sync or attention
 * FOR THE CURRENT ORG/OPERATOR, ordered by capture order (`seq`, FIFO —
 * design "Outbox ordering"), PLUS:
 * - per-item conflict actions on a `rejected` item (RejectedItemCard —
 *   force/edit/discard, wired to `resolveRejected`).
 * - a "taking longer than usual" / "retrying" indicator per item.
 * - a pending-count / oldest-age / storage-usage warning banner
 *   (`storage-guard.ts` wired live, task 7.8).
 * - a DURABLE second section for `synced` items that still carry a warning
 *   worth reviewing (dropped incident photos, a clamped checked-in time) —
 *   these are otherwise only shown on the transient success screen.
 *
 * Scoped by `identity.orgId` ONLY (coordinator finding #4, corrected by the
 * second adversarial review's MEDIUM finding #6, design D12) — a device
 * shared across ORGS must never list another org's items, but items from
 * ANY operator of the SAME org ARE listed for whichever same-org operator
 * is logged in (`capturedByUserId` stays claimed for audit only, never a
 * visibility filter).
 */
export function PendingItemsPage({ identity }: PendingItemsPageProps) {
  const { t } = useTranslation('pending');

  // Re-render periodically so the "taking longer than usual" indicator
  // (computed from `new Date()` vs. an item's own `updatedAt`) does not go
  // stale on a page left open with no Dexie write to trigger a re-render.
  const [nowTick, setNowTick] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNowTick(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  const items = useLiveQuery(async () => {
    if (!identity) return [];
    const rows = (await offlineDb.outbox
      .where('state')
      .anyOf(UNSYNCED_OUTBOX_STATES)
      .toArray()) as unknown as OutboxItem[];
    return rows.filter((row) => row.orgId === identity.orgId).sort((a, b) => a.seq - b.seq);
  }, [identity?.orgId]);

  // Durable warnings — items that already SYNCED but still carry something
  // the operator should review (see hasDurableWarning's own doc comment).
  const durableWarningItems = useLiveQuery(async () => {
    if (!identity) return [];
    const rows = (await offlineDb.outbox
      .where('state')
      .equals('synced')
      .toArray()) as unknown as OutboxItem[];
    return rows
      .filter((row) => row.orgId === identity.orgId && hasDurableWarning(row))
      .sort((a, b) => a.seq - b.seq);
  }, [identity?.orgId]);

  // MEDIUM fix (adversarial review) — `pendingSummary` now feeds the
  // pending-count/oldest-age banners, the `offline.pending` Sentry tag, AND
  // the `offline.queue_stale` staleness check, so ALL THREE must be scoped
  // to items ACTUALLY still waiting to sync on their own
  // (`isActivelyPending` — pending/syncing). `items` itself (rendered
  // below) intentionally stays the FULL UNSYNCED_OUTBOX_STATES list — the
  // operator still needs to see/act on rejected/needs-auth/held rows.
  const activelyPendingItems = (items ?? []).filter(isActivelyPending);
  const pendingSummary = computePendingSummary(activelyPendingItems);
  // task 7.8 — storage-guard wired live. Re-checks whenever the pending
  // count changes (a capture/sync/discard is exactly when the estimate is
  // most likely to have moved).
  const storageGuard = useStorageGuard(pendingSummary.count);

  // MEDIUM fix (adversarial review) — reportQueueStale used to fire on
  // EVERY 30s tick AND every Dexie change for as long as the queue stayed
  // stale, spamming Sentry event volume even though every call shared the
  // same fingerprint (one issue, many events). `queueStaleReported`
  // (module-scope, see its own doc comment above — LOW fix) gates it to
  // ONCE PER STALENESS EPISODE: set the moment it first reports, cleared
  // the moment the queue stops being stale (drained, synced, or every
  // actively-pending item resolved) so a genuinely NEW episode later in
  // the same session — or after a remount — is still reported.

  // task 7.9 (design §9 observability) — offline.pending tag + the
  // offline.queue_stale event. Re-evaluated on every Dexie change AND every
  // 30s tick (nowTick) so a queue that goes stale with NO further writes
  // still gets reported eventually, not just at the instant it crossed the
  // threshold.
  useEffect(() => {
    // LOW fix (adversarial review) — `useLiveQuery` returns `undefined`
    // while its FIRST query is still pending (every fresh mount, including
    // a remount of the same page). Without this guard, that transient
    // "no data yet" instant computed `activelyPendingItems = []` →
    // `stale = false` → reset the module-level gate to `false` a moment
    // before the real (still-stale) data loaded and re-triggered a
    // spurious SECOND report — silently defeating the module-scope fix
    // above for the exact remount case it exists to fix.
    if (items === undefined) return;
    tagPendingCount(pendingSummary.count);
    const stale = isQueueStale(pendingSummary.oldestCapturedAt, nowTick);
    if (!stale) {
      queueStaleReported = false;
      return;
    }
    if (queueStaleReported) return;
    queueStaleReported = true;
    const oldestAgeMs = pendingSummary.oldestCapturedAt
      ? nowTick.getTime() - pendingSummary.oldestCapturedAt.getTime()
      : 0;
    reportQueueStale(oldestAgeMs);
  }, [items, pendingSummary.count, pendingSummary.oldestCapturedAt, nowTick]);

  async function handleForce(id: string) {
    await resolveRejected(outboxRepo, id, { type: 'force' });
  }
  async function handleEditRetry(id: string, patch: Record<string, unknown>) {
    await resolveRejected(outboxRepo, id, { type: 'edit', patch });
  }
  async function handleDiscard(id: string) {
    await resolveRejected(outboxRepo, id, { type: 'discard' });
  }
  // MEDIUM fix (adversarial review) — dismisses a durable warning without
  // discarding the underlying (already-synced) item.
  async function handleAcknowledge(id: string) {
    await acknowledgeDurableWarning(outboxRepo, id);
  }

  const hasNothingToShow =
    (!items || items.length === 0) && (!durableWarningItems || durableWarningItems.length === 0);

  return (
    <div className="space-y-4">
      <PageHeader eyebrow={t('eyebrow')} title={t('title')} description={t('description')} />

      {shouldWarnPendingCount(pendingSummary.count) && (
        <div className="flex items-center gap-2 rounded-sm border border-amber-500/60 bg-amber-500/10 px-3 py-3 text-[13px] text-foreground">
          <AlertTriangle className="size-4 shrink-0 text-amber-600" strokeWidth={1.5} />
          <span>{t('pendingItems.pendingCountWarning', { count: pendingSummary.count })}</span>
        </div>
      )}
      {shouldWarnOldestAge(pendingSummary.oldestCapturedAt) && (
        <div className="flex items-center gap-2 rounded-sm border border-amber-500/60 bg-amber-500/10 px-3 py-3 text-[13px] text-foreground">
          <Clock className="size-4 shrink-0 text-amber-600" strokeWidth={1.5} />
          <span>{t('pendingItems.oldestAgeWarning')}</span>
        </div>
      )}
      {storageGuard.warnUsage && (
        <div className="flex items-center gap-2 rounded-sm border border-amber-500/60 bg-amber-500/10 px-3 py-3 text-[13px] text-foreground">
          <AlertTriangle className="size-4 shrink-0 text-amber-600" strokeWidth={1.5} />
          <span>{t('pendingItems.storageWarning')}</span>
        </div>
      )}

      {hasNothingToShow ? (
        <EmptyState
          icon={CheckCircle2}
          title={t('emptyTitle')}
          description={t('emptyDescription')}
        />
      ) : (
        <>
          {items && items.length > 0 && (
            <ul className="space-y-3">
              {items.map((item) =>
                item.state === 'rejected' ? (
                  <li key={item.id}>
                    <RejectedItemCard
                      item={item}
                      onForce={() => void handleForce(item.id)}
                      onEditRetry={(patch) => void handleEditRetry(item.id, patch)}
                      onDiscard={() => void handleDiscard(item.id)}
                    />
                  </li>
                ) : (
                  <li key={item.id}>
                    <Card className="border-border bg-card">
                      <CardContent className="flex items-center justify-between gap-3 px-4 py-3.5">
                        <div className="min-w-0">
                          <p className="jq-data tabular-nums text-jq-gold-bright">
                            {item.provisionalCode}
                          </p>
                          <p className="jq-mono jq-body-sm truncate text-foreground">
                            {typeof item.payload.plate === 'string' ? item.payload.plate : ''}
                          </p>
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-1">
                          <span className="flex items-center gap-1.5 text-[12px] font-medium uppercase tracking-wide text-muted-foreground">
                            {(item.state === 'pending' || item.state === 'syncing') && (
                              <Loader2
                                className="size-3.5 animate-spin"
                                strokeWidth={2}
                                aria-hidden
                              />
                            )}
                            {t(stateLabelKey(item.state))}
                          </span>
                          {isNearStuckThreshold(
                            item,
                            nowTick,
                            STUCK_SYNCING_RESET_THRESHOLD_MS,
                          ) && (
                            <span className="flex items-center gap-1 text-[11px] text-amber-600">
                              <AlertTriangle className="size-3 shrink-0" strokeWidth={2} />
                              {t('pendingItems.stateStuck')}
                            </span>
                          )}
                          {isBlockedBySubscription(item) && (
                            <span
                              role="alert"
                              className="max-w-[16rem] text-right text-[11px] text-destructive"
                            >
                              {t('pendingItems.subscriptionBlocked')}
                            </span>
                          )}
                          {isRetrying(item) && !isBlockedBySubscription(item) && (
                            <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                              <RefreshCw className="size-3 shrink-0" strokeWidth={2} />
                              {t('pendingItems.stateRetrying')}
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </li>
                ),
              )}
            </ul>
          )}

          {durableWarningItems && durableWarningItems.length > 0 && (
            <div className="space-y-3">
              <h2 className="jq-eyebrow text-muted-foreground">
                {t('pendingItems.durableWarningsTitle')}
              </h2>
              <ul className="space-y-3">
                {durableWarningItems.map((item) => (
                  <li key={item.id}>
                    <Card className="border-amber-600/40 bg-card">
                      <CardContent className="space-y-1.5 px-4 py-3.5">
                        {item.result?.incidentPhotosDropped && (
                          <p className="jq-body-sm text-foreground">
                            {item.result.incidentPhotosDropped.code === 'feature_not_in_plan'
                              ? t('pendingItems.durableIncidentPhotosDroppedPlan', {
                                  ticketNumber: item.result.ticketNumber,
                                })
                              : t('pendingItems.durableIncidentPhotosDroppedGeneric', {
                                  ticketNumber: item.result.ticketNumber,
                                  reason: item.result.incidentPhotosDropped.code,
                                })}
                          </p>
                        )}
                        {item.result?.checkinTimeSource === 'offline_capped' && (
                          <p className="jq-body-sm text-foreground">
                            {t('pendingItems.durableCheckinTimeAdjusted', {
                              ticketNumber: item.result.ticketNumber,
                              time: item.result.checkedInAt
                                ? new Date(item.result.checkedInAt).toLocaleTimeString()
                                : '',
                            })}
                          </p>
                        )}
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => void handleAcknowledge(item.id)}
                        >
                          {t('pendingItems.actionAcknowledge')}
                        </Button>
                      </CardContent>
                    </Card>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
